import { useEffect, useState } from 'react';
import type { EditProfileFormProps } from './formPorps';
import { Box, Button, Chip, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FormField from 'app/components/FormField';
import { useFormContext } from 'react-hook-form';
import styles from './EditProfileForm.module.css';
import {
  SENIORITY_OPTIONS,
  MY_FOCUS_OPTIONS,
  MAIN_SERVICES_OPTIONS,
  QUALIFICATIONS_OPTIONS,
} from 'constants/editProfile';
import Container from 'app/components/Container';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { toast } from 'react-toastify';
import VSpace from 'app/components/VSpace';
import { getTags } from 'utils';
import PopupInput from 'app/components/Input/PopupInput/PopupInput';
import { CloseRounded } from '@mui/icons-material';

function Business({ back, next }: EditProfileFormProps) {
  const { t } = useTranslation();
  const { control, trigger, getValues, setValue, watch } = useFormContext();
  const [popupInputOpen, setPopupInputOpen] = useState(false);

  // 為了 Rerender Chip
  const watchMainProductsServices = watch('mainProductsServices', []);
  const watchCustomizedProductsOrServices = watch(
    'customizedProductsOrServices',
    '',
  );
  const customizedProductsOrServicesTags = getTags(
    watchCustomizedProductsOrServices,
  );

  const watchIsMDRT = watch('isMDRT', []);
  const watchSeniority = watch('seniority', '');

  const handleNextOnClick = async () => {
    const validateResult = await trigger([
      'company',
      'jobTitle',
      'seniority',
      'shouldShowLicense',
      'license',
      'isMDRT',
      'otherAwards',
      'jobExperience',
    ]);
    const watchMainProductsServices = watch('mainProductsServices', []);
    const watchCustomizedProductsOrServices = watch(
      'customizedProductsOrServices',
      '',
    );
    if (
      validateResult &&
      (watchMainProductsServices.length > 0 ||
        watchCustomizedProductsOrServices !== '')
    ) {
      next!();
    } else {
      const error = t('AdviserProfile.Error.Required');
      toast.error(error);
    }
  };

  const handleSeniorityChipOnClick = val => {
    setValue('seniority', val);
  };

  const handleMainProductsServicesChipOnClick = val => {
    const currentLength = getTotalLength();

    const mainProductsServices = getValues('mainProductsServices');
    if (mainProductsServices.includes(val)) {
      setValue(
        'mainProductsServices',
        mainProductsServices.filter(tag => tag !== val),
      );
    } else if (currentLength > 5) {
      const tr = t('AdviserProfile.AddCustomLabel.MdaMcMaxReachedHint');
      toast.error(tr);
      return;
    } else {
      setValue('mainProductsServices', [...mainProductsServices, val]);
    }
  };

  const handleCustomizedProductsOrServicesValue = val => {
    const customizedProductsOrServices = getValues(
      'customizedProductsOrServices',
    );
    console.log(
      'ori customizedProductsOrServices ::: ',
      customizedProductsOrServices,
    );
    setValue(
      'customizedProductsOrServices',
      customizedProductsOrServices + '#' + val,
    );
  };

  const handleRemoveCustomizedProductsOrServicesValue = val => {
    const customizedProductsOrServices = getValues(
      'customizedProductsOrServices',
    );
    var nextVal: string;
    if (customizedProductsOrServices.includes(`#${val}`)) {
      nextVal = customizedProductsOrServices.replace(`#${val}`, '');
    } else {
      nextVal = customizedProductsOrServices.replace(`${val}`, '');
    }
    setValue('customizedProductsOrServices', nextVal);
  };

  const handleIsMDRTChipOnClick = val => {
    const isMDRTArr = getValues('isMDRT');
    if (isMDRTArr.includes(val)) {
      setValue(
        'isMDRT',
        isMDRTArr.filter(language => language !== val),
      );
    } else {
      setValue('isMDRT', [...isMDRTArr, val]);
    }
  };

  const license = watch('license');
  useEffect(() => {
    if (!license || license.replaceAll('\n', '').replaceAll(' ', '') === '') {
      setValue('license', t('AdviserProfile.DefaultLicense'));
    }
  }, [license, t, setValue]);

  const getTotalLength = () => {
    const countMain = watchMainProductsServices.length;
    const countCustomized =
      getTags(watchCustomizedProductsOrServices)?.length ?? 0;
    return countMain + countCustomized;
  };
  const totalLength = getTotalLength();

  return (
    <div className={styles.container}>
      <Container
        sx={{
          padding: { xs: '16px', md: '24px' },
          marginBottom: '16px',
        }}
      >
        <>
          <Box className={styles.fieldsGroup}>
            <Box>
              <FormField
                type="text"
                label={t('AdviserProfile.Workplace')}
                name="company"
                control={control}
              />
            </Box>
            <Box>
              <FormField
                type="text"
                label={t('AdviserProfile.JobTitle')}
                name="jobTitle"
                control={control}
              />
            </Box>
          </Box>
          <Typography
            sx={{
              marginBottom: '16px',
              fontSize: 14,
            }}
          >
            {t('AdviserProfile.Seniority')}
          </Typography>
          <Box>
            {SENIORITY_OPTIONS.map(option => (
              <Chip
                key={option.label}
                className={clsx(
                  watchSeniority && watchSeniority.includes(option.value)
                    ? styles.inactive
                    : undefined,
                )}
                label={t(`AdviserProfile.SeniorityTag.${option.value}`)}
                variant="outlined"
                sx={{
                  margin: '0 8px 8px 0',
                }}
                onClick={() => handleSeniorityChipOnClick(option.value)}
              />
            ))}
          </Box>

          <Box>
            <Box
              display="flex"
              justifyContent="space-between"
              alignContent="center"
            >
              <Box display="flex" alignContent="center" flexWrap="wrap">
                {t('AdviserProfile.License')}
              </Box>
              <FormField
                type="switch"
                label={t('AdviserProfile.ShouldShowLicense')}
                name="shouldShowLicense"
                control={control}
                hideSpacing
                customFieldStyle={{
                  fontSize: 14,
                }}
              />
            </Box>
            <FormField
              type="text"
              label=""
              name="license"
              placeholder="License"
              control={control}
              multiline
              minRows={2}
            />
          </Box>
        </>
      </Container>
      <Container
        sx={{
          padding: { xs: '16px', md: '24px' },
          marginBottom: '16px',
        }}
      >
        <>
          <Typography variant="subtitle2" color="var(--color-text-primary)">
            {t('AdviserProfile.MyFocus')}{' '}
            <span className={styles.required}>*</span>
            <span className={styles.remark}>
              ({t('AdviserProfile.Select')}{' '}
              <span className={styles.required}>
                {t('AdviserProfile.UpTo6InTotal')}
              </span>
            </span>
            )
          </Typography>
          <Typography
            className={styles.subtitle}
            variant="caption"
            fontWeight="500"
          >
            🛒 {t('AdviserProfile.MainProducts')}
          </Typography>
          {MY_FOCUS_OPTIONS.map(option => (
            <Chip
              key={option.value}
              className={clsx(
                watchMainProductsServices &&
                  watchMainProductsServices.includes(option.value)
                  ? styles.inactive
                  : undefined,
              )}
              label={t(
                `AdviserProfile.MainProductsAndServices.${option.value}`,
              )}
              variant="outlined"
              sx={{
                margin: '0 8px 8px 0',
              }}
              onClick={() =>
                handleMainProductsServicesChipOnClick(option.value)
              }
            />
          ))}
          <Typography
            className={styles.subtitle}
            variant="caption"
            fontWeight="500"
          >
            💻 {t('AdviserProfile.MainServices')}
          </Typography>
          {MAIN_SERVICES_OPTIONS.map(option => (
            <Chip
              key={option.value}
              className={clsx(
                watchMainProductsServices &&
                  watchMainProductsServices.includes(option.value)
                  ? styles.inactive
                  : undefined,
              )}
              label={t(
                `AdviserProfile.MainProductsAndServices.${option.value}`,
              )}
              variant="outlined"
              sx={{
                margin: '0 8px 8px 0',
              }}
              onClick={() =>
                handleMainProductsServicesChipOnClick(option.value)
              }
            />
          ))}
          <Typography
            className={styles.subtitle}
            variant="caption"
            fontWeight="500"
          >
            💻 {t('AdviserProfile.CustomizedProductsOrServices')}
          </Typography>
          {[
            ...customizedProductsOrServicesTags.map(option => (
              <Chip
                key={option}
                className={styles.inactive}
                label={option}
                variant="outlined"
                deleteIcon={<CloseRounded />}
                sx={{
                  margin: '0 8px 8px 0',
                  fontWeight: 'bold',
                  '& .MuiChip-deleteIcon': {
                    color: '#fff',
                    fontSize: '12px',
                  },
                }}
                onClick={() => {
                  handleRemoveCustomizedProductsOrServicesValue(option);
                }}
                onDelete={() => {
                  handleRemoveCustomizedProductsOrServicesValue(option);
                }}
              />
            )),
            <Chip
              key="add-tag"
              label={t('AdviserProfile.AddCustomLabel.label')}
              variant="outlined"
              onClick={() => {
                if (totalLength > 5) {
                  const tr = t(
                    'AdviserProfile.AddCustomLabel.MdaMcMaxReachedHint',
                  );
                  toast.error(tr);
                } else {
                  setPopupInputOpen(true);
                }
              }}
              sx={{
                margin: '0 8px 8px 0',
              }}
            />,
          ]}
          <VSpace size={3} />
          <PopupInput
            open={popupInputOpen}
            onClose={() => setPopupInputOpen(false)}
            onValue={handleCustomizedProductsOrServicesValue}
            title={t('AdviserProfile.AddCustomLabel.MdaAddCustomTagTitle')}
            description={t(
              'AdviserProfile.AddCustomLabel.MdaAddCustomTagDescription',
            )}
            cta={t('AdviserProfile.AddCustomLabel.MdaAddCustomTagConfirmCta')}
          />
        </>
      </Container>
      <Container
        sx={{
          padding: { xs: '16px', md: '24px' },
        }}
      >
        <>
          <Typography
            variant="subtitle2"
            color="var(--color-text-primary)"
            sx={{
              marginBottom: '16px',
            }}
          >
            {t('AdviserProfile.Qualifications')}
          </Typography>
          <Box
            sx={{
              marginBottom: '16px',
            }}
          >
            {QUALIFICATIONS_OPTIONS.map(option => (
              <Chip
                key={option.value}
                className={clsx(
                  watchIsMDRT && watchIsMDRT.includes(option.value)
                    ? styles.inactive
                    : undefined,
                )}
                label={t(`AdviserProfile.Qualification.${option.value}`)}
                variant="outlined"
                sx={{
                  margin: '0 8px 8px 0',
                }}
                onClick={() => handleIsMDRTChipOnClick(option.value)}
              />
            ))}
          </Box>
          <FormField
            type="text"
            label={t('AdviserProfile.Achievement')}
            name="otherAwards"
            placeholder={t('AdviserProfile.Placeholder.Achievement')}
            control={control}
            multiline
            minRows={2}
          />
          <FormField
            type="text"
            label={t('AdviserProfile.JobExperience')}
            name="jobExperience"
            placeholder={t('AdviserProfile.Placeholder.JobExperience')}
            control={control}
            multiline
            minRows={2}
          />
          <Box
            className={styles.footer}
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              sx={{
                minWidth: { xs: 'calc(50% - 12px)', md: '120px' },
                boxShadow: { xs: 'var(--shadow-l6)', md: 'unset' },
              }}
              onClick={back}
            >
              {t('Global.Back')}
            </Button>
            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              sx={{
                minWidth: { xs: 'calc(50% - 12px)', md: '120px' },
                boxShadow: { xs: 'var(--shadow-l6)', md: 'unset' },
              }}
              onClick={handleNextOnClick}
            >
              {t('Global.Next')}
            </Button>
          </Box>
        </>
      </Container>
    </div>
  );
}

export default Business;
